import { useNavigate } from 'react-router-dom';
import { useListLabOrdersV1LabsOrdersGet } from '../../../api/generated-catch-backend/laboratory-orders/laboratory-orders';
import { TopLevelOrderStatus } from '../../../api/generated-catch-backend/model';
import { ROUTE_LABS, ROUTE_LABS_ORDER_STEP_1 } from '../../../constants/routes';
import '../dashboard/dashboard-reset.css';
import useAnalyticsCalls, { DashboardSource } from '@/hooks/use-analytics';

interface CatchLabsCTAProps {
  videoSrc?: string;
}

export const CatchLabsCTA = ({ videoSrc = '/videos/catch-hero.mp4' }: CatchLabsCTAProps) => {
  const navigate = useNavigate();
  const { setNavigationSource } = useAnalyticsCalls();

  // Fetch user's lab orders
  const { data: ordersData, isLoading } = useListLabOrdersV1LabsOrdersGet();

  const topLevelStatus = ordersData?.meta.status;

  // Don't show CTA for results ready (user has results)
  if (topLevelStatus === TopLevelOrderStatus.results_ready) {
    return null;
  }

  // Show loading state
  if (isLoading) {
    return null;
  }

  const handleClick = () => {
    setNavigationSource(DashboardSource.DASHBOARD_LABS_CTA);
    if (topLevelStatus === TopLevelOrderStatus.eligible) {
      navigate(ROUTE_LABS_ORDER_STEP_1);
    } else {
      navigate(ROUTE_LABS);
    }
  };

  // Content configuration based on status
  // Note: pending_review is intentionally excluded because we don't show it in the UI
  const content = {
    [TopLevelOrderStatus.eligible]: {
      title: 'Book Your Lab Visit',
      description:
        'Get clinical lab results integrated into your unique cancer risk profile for a more accurate view of your health. Annual test of 50+ biomarkers included in your membership at no extra cost.',
      buttonText: 'Get Started',
    },
    [TopLevelOrderStatus.ready_for_testing]: {
      title: 'Your Labs Are In Progress',
      description:
        "Your labs are underway. If you haven't gone yet, visit the location you selected anytime for your blood draw. If you've already gone, your results will appear here as soon as they're ready.",
      buttonText: 'View Labs Order',
    },
  } as const;

  // Type guard to check if status is one we have content for
  function hasContent(status: string | undefined): status is keyof typeof content {
    return status !== undefined && status in content;
  }

  const currentContent = hasContent(topLevelStatus)
    ? content[topLevelStatus]
    : topLevelStatus === TopLevelOrderStatus.pending_review
      ? content[TopLevelOrderStatus.ready_for_testing]
      : content[TopLevelOrderStatus.eligible];

  return (
    <div className="dashboard-reset relative w-full overflow-hidden rounded-[10px] bg-[#183344] mb-6">
      {/* Background Video */}
      <video
        src={videoSrc}
        autoPlay
        loop
        muted
        playsInline
        className="absolute left-0 top-0 z-0 h-full w-full object-cover"
      />

      {/* Dark overlay */}
      <div className="absolute left-0 top-0 z-[1] h-full w-full bg-black/65" />

      {/* Content */}
      <div className="relative z-10 flex h-full flex-col items-center justify-center gap-4 p-8 text-center">
        {/* Title */}
        <h3 className="mb-1 mt-6 text-center text-[22px] font-bold leading-[1.3] tracking-[0] text-white">
          {currentContent.title}
        </h3>

        {/* Description */}
        <p className="max-w-[600px] text-base font-normal leading-relaxed text-white">
          {currentContent.description}
        </p>

        {/* CTA Button */}
        <button
          onClick={handleClick}
          className="mb-6 mt-2 flex h-10 w-full max-w-sm items-center justify-center rounded-full bg-catch-avocado-400 px-6 text-sm font-bold text-catch-dark-blue-90 shadow-lg transition-all hover:scale-105 hover:shadow-xl"
        >
          {currentContent.buttonText}
        </button>
      </div>
    </div>
  );
};
